import React, { useMemo } from 'react'

import { ChainId, getChainNameFromChainId } from '@src/configs/chainInfo'
import CompactTokensList from '@src/pages/Exchange/components/TokenSelectorModal/components/CompactTokensList'
import { sortTokensByPriority } from '@src/pages/Exchange/components/TokenSelectorModal/utils'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'

import CompactTokensListItem from './CompactTokensListItem'

interface TokenCategoriesDisplayProps {
    displayedTokens: IToken[]
    selectedToken?: IToken
    onTokenClicked: (token: IToken) => void
    getTokenBalance?: (token: IToken) => { tokenBalance: string; usdBalance: string } | undefined
    // The token that is currently selected in the app (not just highlighted in the modal)
    currentlySelectedToken?: IToken
    // Chain selection states
    temporaryChainSelection?: number
    activeChainSelection?: number
    // The selected chain from the parent component
    selectedChain?: string | number
    // Search query
    tokenQuery?: string
}

export const TokenCategoriesDisplay: React.FC<TokenCategoriesDisplayProps> = ({
    displayedTokens,
    selectedToken,
    onTokenClicked,
    getTokenBalance,
    currentlySelectedToken,
    temporaryChainSelection,
    activeChainSelection,
    selectedChain,
    tokenQuery = '', // Default to empty string
}) => {
    // Get the current selected chain ID for strict filtering
    const currentChainId = useMemo(() => {
        return temporaryChainSelection || activeChainSelection || Number(selectedChain) || 0
    }, [temporaryChainSelection, activeChainSelection, selectedChain])

    // Create a unique token identifier
    const getTokenId = (token: IToken) => `${token.chainId}-${token.address.toLowerCase()}`

    // Strictly filter tokens to match the current chain ID
    const strictlyFilteredTokens = useMemo(() => {
        // First filter by chain ID
        const chainFiltered = displayedTokens.filter(token => Number(token.chainId) === currentChainId)

        // In testnet mode, ensure no duplicates by using a Map
        if (isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')) {
            const uniqueMap = new Map<string, IToken>()

            chainFiltered.forEach(token => {
                uniqueMap.set(getTokenId(token), token)
            })

            return Array.from(uniqueMap.values())
        }

        return chainFiltered
    }, [displayedTokens, currentChainId])

    // Only include tokens with actual balance in the "Your tokens" section
    const tokensWithBalance = strictlyFilteredTokens.filter(
        token => token.userBalanceInUSD && token.userBalanceInUSD > 0
    )
    const tokensWithoutBalance = strictlyFilteredTokens.filter(
        token => !token.userBalanceInUSD || token.userBalanceInUSD === 0
    )

    // Check if the current token matches the search query
    const currentTokenMatchesSearch = (() => {
        if (!currentlySelectedToken || !tokenQuery) return true

        const query = tokenQuery.toLowerCase()
        return (
            currentlySelectedToken.name.toLowerCase().includes(query) ||
            currentlySelectedToken.symbol.toLowerCase().includes(query) ||
            currentlySelectedToken.address.toLowerCase() === query
        )
    })()

    // Check if current token has balance
    const currentTokenHasBalance = (() => {
        if (!currentlySelectedToken) return false

        // First check the userBalanceInUSD property
        if (currentlySelectedToken.userBalanceInUSD && currentlySelectedToken.userBalanceInUSD > 0) {
            return true
        }

        // If userBalanceInUSD is not set, use getTokenBalance function
        if (getTokenBalance) {
            const balance = getTokenBalance(currentlySelectedToken)
            if (balance && balance.usdBalance && parseFloat(balance.usdBalance) > 0) {
                return true
            }
        }

        return false
    })()

    // Check if current token is on the selected chain
    const isCurrentTokenOnSelectedChain = (() => {
        if (!currentlySelectedToken) return false
        return temporaryChainSelection || activeChainSelection
            ? currentlySelectedToken.chainId === (temporaryChainSelection || activeChainSelection)
            : currentlySelectedToken.chainId === Number(selectedChain)
    })()

    // Helper function to check if currently selected token should be shown separately
    const shouldShowCurrentTokenSeparately = (hasBalance: boolean): boolean => {
        return !!(
            currentlySelectedToken &&
            currentTokenMatchesSearch &&
            isCurrentTokenOnSelectedChain &&
            currentTokenHasBalance === hasBalance
        )
    }

    // Helper function to filter out currently selected token if it will be shown separately
    const filterOutCurrentTokenIfNeeded = (tokens: IToken[], shouldFilter: boolean): IToken[] => {
        if (!currentlySelectedToken) {
            return tokens
        }

        // In testnet mode, always filter out the currently selected token to avoid duplicates
        const isTestnetMode = isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')
        const shouldFilterToken = isTestnetMode || shouldFilter

        if (!shouldFilterToken) {
            return tokens
        }

        return tokens.filter(token => {
            if (!token) return true
            return getTokenId(token) !== getTokenId(currentlySelectedToken)
        })
    }

    // Helper function to get count including currently selected token if applicable
    const getTokenCountWithCurrentToken = (baseCount: number, hasBalance: boolean): number => {
        const shouldIncludeCurrentToken = shouldShowCurrentTokenSeparately(hasBalance)
        return baseCount + (shouldIncludeCurrentToken ? 1 : 0)
    }

    // Filter out the currently selected token from other lists to avoid duplication
    // Only filter it out if it will be shown separately at the top
    const tokensWithBalanceFiltered = filterOutCurrentTokenIfNeeded(
        tokensWithBalance,
        shouldShowCurrentTokenSeparately(true)
    )

    const tokensWithoutBalanceFiltered = filterOutCurrentTokenIfNeeded(
        tokensWithoutBalance,
        shouldShowCurrentTokenSeparately(false)
    )

    // Get the current chain name for display
    const currentChainName = (() => {
        const chainId = temporaryChainSelection || activeChainSelection || Number(selectedChain)
        const chainName = getChainNameFromChainId(chainId as ChainId)
        // Only capitalize the first letter of the chain name
        // If bsc, return "BSC"
        const formattedChainName = chainName.charAt(0).toUpperCase() + chainName.slice(1)
        if (formattedChainName === 'Bsc') return 'BSC'
        else if (formattedChainName === 'Bitcoin') return 'Bitcoin'
        else if (formattedChainName === 'Megaethtestnet') return 'MegaETH Testnet'
        else if (formattedChainName === 'Monadtestnet') return 'Monad Testnet'
        return formattedChainName
    })()

    return (
        <div className='animate-fadeIn space-y-4'>
            {/* Tokens with balances */}
            {/* Only show Your Tokens section if there are tokens with balances or the current token has balance */}
            {(tokensWithBalanceFiltered.length > 0 || shouldShowCurrentTokenSeparately(true)) && (
                <div className='space-y-1'>
                    <div className='px-2'>
                        <h3 className='text-sm font-medium text-token-modal-label-text'>
                            Your tokens ({getTokenCountWithCurrentToken(tokensWithBalanceFiltered.length, true)})
                        </h3>
                    </div>

                    {/* Show currently selected token at the top if it exists, matches search, is on the same chain, AND has balance */}
                    {shouldShowCurrentTokenSeparately(true) && (
                        <div className='mb-2'>
                            <CompactTokensListItem
                                key={`${currentlySelectedToken!.chainId}-${currentlySelectedToken!.address}`}
                                selectedToken={selectedToken}
                                token={currentlySelectedToken!}
                                onTokenClicked={onTokenClicked}
                                userBalanceInTokens={getTokenBalance?.(currentlySelectedToken!)?.tokenBalance}
                                userBalanceInUsd={getTokenBalance?.(currentlySelectedToken!)?.usdBalance}
                                isCurrentlySelected={true}
                            />
                        </div>
                    )}

                    {tokensWithBalanceFiltered.length > 0 && (
                        <CompactTokensList
                            tokens={sortTokensByPriority(tokensWithBalanceFiltered)}
                            selectedToken={selectedToken}
                            onTokenClicked={onTokenClicked}
                            getTokenBalance={getTokenBalance}
                            currentlySelectedToken={currentlySelectedToken}
                        />
                    )}
                </div>
            )}

            {/* Chain specific tokens */}
            <div className='space-y-1'>
                <div className='px-2'>
                    <h3 className='text-sm font-medium text-token-modal-label-text'>
                        {`${currentChainName} tokens (${getTokenCountWithCurrentToken(tokensWithoutBalanceFiltered.length, false)})`}
                    </h3>
                </div>

                {/* Show currently selected token at the top if it exists, matches search, is on the same chain, but has NO balance */}
                {shouldShowCurrentTokenSeparately(false) && (
                    <div className='mb-2'>
                        <CompactTokensListItem
                            key={`${currentlySelectedToken!.chainId}-${currentlySelectedToken!.address}`}
                            selectedToken={selectedToken}
                            token={currentlySelectedToken!}
                            onTokenClicked={onTokenClicked}
                            userBalanceInTokens={getTokenBalance?.(currentlySelectedToken!)?.tokenBalance}
                            userBalanceInUsd={getTokenBalance?.(currentlySelectedToken!)?.usdBalance}
                            isCurrentlySelected={true}
                        />
                    </div>
                )}

                {tokensWithoutBalanceFiltered.length > 0 && (
                    <CompactTokensList
                        tokens={sortTokensByPriority(tokensWithoutBalanceFiltered)}
                        selectedToken={selectedToken}
                        onTokenClicked={onTokenClicked}
                        getTokenBalance={getTokenBalance}
                        currentlySelectedToken={currentlySelectedToken}
                    />
                )}
            </div>
        </div>
    )
}

export default TokenCategoriesDisplay
